// frontend/src/features/aw/AWAgentManager.jsx
import React, { useEffect, useState } from "react";
import {
  getAgentConfig, addAgentConfig, deleteAgentConfig,
  getFormDefaults, updateFormDefaults, getSupportedCads,
} from "./api";

export default function AWAgentManager() {
  const [configs, setConfigs] = useState([]);
  const [cadOptions, setCadOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameVal, setNameVal] = useState("");
  const [codeCad, setCodeCad] = useState("");
  const [codeVal, setCodeVal] = useState("");
  const [defaults, setDefaults] = useState({});
  const [defaultsDirty, setDefaultsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [cfg, defs] = await Promise.all([getAgentConfig(), getFormDefaults()]);
      setConfigs(cfg);
      setDefaults(defs);
      setDefaultsDirty(false);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    getSupportedCads().then(d => {
      const cads = (d.supported || []).sort();
      setCadOptions(cads);
      if (cads.length && !codeCad) setCodeCad(cads[0]);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const namePatterns = configs.filter(c => c.identifier_type === "name");
  const cadCodes = configs.filter(c => c.identifier_type === "code");

  // Group codes by cad_key
  const codeGroups = {};
  cadCodes.forEach(c => {
    const k = c.cad_key || "unknown";
    if (!codeGroups[k]) codeGroups[k] = [];
    codeGroups[k].push(c);
  });

  const handleAddName = async () => {
    if (!nameVal.trim()) return;
    await addAgentConfig({ identifier_type: "name", value: nameVal.trim(), label: `Name match: ${nameVal.trim()}` });
    setNameVal("");
    await load();
  };

  const handleAddCode = async () => {
    if (!codeVal.trim()) return;
    await addAgentConfig({ identifier_type: "code", cad_key: codeCad, value: codeVal.trim(), label: `${codeCad.charAt(0).toUpperCase() + codeCad.slice(1)} agent code` });
    setCodeVal("");
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this identifier?")) return;
    await deleteAgentConfig(id);
    await load();
  };

  const handleDefaultChange = (key, val) => {
    setDefaults(d => ({ ...d, [key]: val }));
    setDefaultsDirty(true);
  };

  const handleSaveDefaults = async () => {
    setSaving(true);
    try {
      const updated = await updateFormDefaults(defaults);
      setDefaults(updated);
      setDefaultsDirty(false);
    } catch {} finally { setSaving(false); }
  };

  const isEmpty = configs.length === 0 && !loading;

  return (
    <div className="aw-am">
      <style>{AM_CSS}</style>

      {/* Header */}
      <div className="aw-am-header">
        <div className="aw-am-header-left">
          <div className="aw-am-icon-wrap">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0"/>
            </svg>
          </div>
          <div>
            <h2 className="aw-am-title">Agent Identifiers</h2>
            <p className="aw-am-sub">{namePatterns.length} name patterns &middot; {cadCodes.length} CAD codes</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="aw-am-empty">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ animation: "aw-spin 1s linear infinite" }}>
            <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93"/>
          </svg>
          <div style={{ fontSize: 13, color: "var(--dim)", marginTop: 8 }}>Loading...</div>
        </div>
      ) : (
        <>
          {isEmpty && (
            <div className="aw-am-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="1.2">
                <path d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0"/>
              </svg>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}>No agent identifiers configured</div>
              <div style={{ fontSize: 12, color: "var(--dim)" }}>Use the forms below to add your agent name patterns and CAD codes.</div>
            </div>
          )}
          {/* Section 1: Name Patterns */}
          <div className="aw-am-section">
            <h3 className="aw-am-section-title">Agent Name Patterns</h3>
            <p className="aw-am-section-desc">Matched against agent names on all CAD sites (case-insensitive substring).</p>
            <div className="aw-am-list">
              {namePatterns.map(c => (
                <div key={c.id} className="aw-am-item">
                  <div className="aw-am-item-badge">N</div>
                  <div className="aw-am-item-info">
                    <span className="aw-am-item-val">{c.value}</span>
                  </div>
                  <button className="aw-act aw-act-red" onClick={() => handleDelete(c.id)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="aw-am-add-row">
              <input className="aw-input" placeholder="Name pattern (e.g. BA Property Tax)" value={nameVal} onChange={e => setNameVal(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAddName()} style={{ flex: 1 }} />
              <button className="aw-btn-primary" onClick={handleAddName} disabled={!nameVal.trim()} style={{ padding: "8px 16px" }}>Add</button>
            </div>
          </div>

          {/* Section 2: CAD Agent Codes */}
          <div className="aw-am-section">
            <h3 className="aw-am-section-title">CAD Agent Codes</h3>
            <p className="aw-am-section-desc">Per-CAD numeric/alpha codes used to identify the agent on specific county appraisal districts.</p>
            <div className="aw-am-list">
              {Object.keys(codeGroups).sort().map(cad => (
                codeGroups[cad].map(c => (
                  <div key={c.id} className="aw-am-item">
                    <div className="aw-am-item-badge aw-am-item-badge-code">{(c.cad_key || "?").slice(0, 3).toUpperCase()}</div>
                    <div className="aw-am-item-info">
                      <span className="aw-am-item-cad">{(c.cad_key || "").charAt(0).toUpperCase() + (c.cad_key || "").slice(1)}</span>
                      <span className="aw-am-item-val">{c.value}</span>
                    </div>
                    <button className="aw-act aw-act-red" onClick={() => handleDelete(c.id)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))
              ))}
            </div>
            <div className="aw-am-add-row">
              <select className="aw-sel" value={codeCad} onChange={e => setCodeCad(e.target.value)} style={{ minWidth: 120 }}>
                {cadOptions.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
              <input className="aw-input" placeholder="Agent code (e.g. B35)" value={codeVal} onChange={e => setCodeVal(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAddCode()} style={{ flex: 1 }} />
              <button className="aw-btn-primary" onClick={handleAddCode} disabled={!codeVal.trim()} style={{ padding: "8px 16px" }}>Add</button>
            </div>
          </div>

          {/* Section 3: AOA Form Defaults */}
          <div className="aw-am-section">
            <h3 className="aw-am-section-title">AOA Form Defaults</h3>
            <p className="aw-am-section-desc">Default agent info auto-populated on AOA forms.</p>
            <div className="aw-am-defaults">
              {[
                ["agent_name", "Agent Name"],
                ["agent_phone", "Phone"],
                ["agent_address", "Address"],
                ["agent_city_state_zip", "City, State, Zip"],
              ].map(([key, label]) => (
                <div key={key} style={{ marginBottom: 8 }}>
                  <label className="aw-label">{label}</label>
                  <input className="aw-input" style={{ width: "100%" }} value={defaults[key] || ""}
                    onChange={e => handleDefaultChange(key, e.target.value)} />
                </div>
              ))}
              <button className="aw-btn-primary" onClick={handleSaveDefaults} disabled={!defaultsDirty || saving}
                style={{ marginTop: 4 }}>
                {saving ? "Saving..." : "Save Defaults"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const AM_CSS = `
.aw-am{max-width:800px;margin:0 auto;animation:aw-in .4s var(--ease) both;}

.aw-am-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;}
.aw-am-header-left{display:flex;align-items:center;gap:12px;}
.aw-am-icon-wrap{
  width:44px;height:44px;border-radius:10px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:var(--accent-g);border:1px solid rgba(139,35,50,.2);
}
.aw-am-title{font-size:20px;font-weight:800;margin:0;line-height:1.2;}
.aw-am-sub{font-size:13px;color:var(--dim);margin:3px 0 0;}

.aw-am-empty{
  padding:60px 24px;text-align:center;color:var(--muted);
  border-radius:10px;background:var(--surface);border:1px solid var(--border-l);
}

.aw-am-section{
  margin-bottom:24px;padding:16px;border-radius:10px;
  background:var(--surface);border:1px solid var(--border-l);
}
.aw-am-section-title{font-size:14px;font-weight:700;margin:0 0 2px;}
.aw-am-section-desc{font-size:11px;color:var(--dim);margin:0 0 12px;}

.aw-am-list{display:grid;gap:4px;margin-bottom:12px;}
.aw-am-item{
  display:flex;align-items:center;gap:10px;padding:8px 10px;
  border-radius:7px;background:rgba(27,25,30,.3);border:1px solid var(--border-l);
  transition:border-color .15s var(--ease);
}
.aw-am-item:hover{border-color:var(--border);}

.aw-am-item-badge{
  width:28px;height:28px;border-radius:6px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:var(--accent-g);border:1px solid rgba(139,35,50,.2);
  font-size:10px;font-weight:800;color:var(--accent);
}
.aw-am-item-badge-code{
  background:rgba(74,142,245,.06);border-color:rgba(74,142,245,.12);
  color:#7db5f0;
}

.aw-am-item-info{flex:1;display:flex;align-items:center;gap:8px;min-width:0;}
.aw-am-item-val{font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.aw-am-item-cad{
  font-size:10px;font-weight:700;color:var(--dim);text-transform:uppercase;letter-spacing:.5px;
  padding:2px 6px;border-radius:3px;background:rgba(107,101,119,.08);border:1px solid var(--border-l);
  flex-shrink:0;
}

.aw-am-add-row{display:flex;gap:8px;align-items:center;}

.aw-am-defaults{max-width:400px;}
`;
